/**
 * The forty-two Epics — `DESIGN.md` §9, transcribed.
 *
 * These are the permanent answers at the end of each direction (D §8.2): one
 * of three, mutually exclusive, and explicitly **not respec-able**. D §9
 * calls them "prototype balance packages… intentionally concrete so
 * implementation can expose the required simulation variables and tests", and
 * that is exactly what they are used for here — every line of every package
 * becomes an `Effect` naming a variable, and `tools/godhood.ts` reads the
 * union of those variables as the simulation's work list.
 *
 * They are transcribed rather than summarised because their whole value is
 * being concrete. Where D §9 states a tradeoff in prose ("no bonus against
 * healthy targets", "direct coercion invalidates the bonus") it becomes a
 * `rule` effect with value 0, so the tradeoff cannot be quietly dropped when
 * somebody implements the numbers.
 *
 * The numbers are D §9's own and are prototype targets, not tuning. Nothing
 * here has been measured; when one of them is implemented it gets measured
 * and `ROADMAP.md` records what it actually cost.
 *
 * Pure by law 7.
 */

import type { DirectionId, Effect, Epic } from './types';

const mul = (variable: string, value: number, observed: string): Effect =>
  ({ variable, kind: 'multiplier', value, observed });
const cost = (variable: string, value: number, observed: string): Effect =>
  ({ variable, kind: 'cost', value, observed });
const add = (variable: string, value: number, observed: string): Effect =>
  ({ variable, kind: 'add', value, observed });
const cap = (variable: string, value: number, observed: string): Effect =>
  ({ variable, kind: 'cap', value, observed });
const rule = (variable: string, observed: string): Effect =>
  ({ variable, kind: 'rule', value: 0, observed });

const epic = (
  direction: DirectionId, slug: string, name: string,
  identity: string, effects: readonly Effect[],
): Epic => ({ id: `${direction}.epic.${slug}`, direction, name, identity, effects });

export const EPICS: readonly Epic[] = [

  /* ── Life ────────────────────────────────────────────────────────────── */

  epic('life', 'deep-green', 'The Deep Green', 'Persistence / endurance.', [
    mul('life.effect.decayRate', 0.5, 'what you grow stays green through seasons that would have taken it back'),
    mul('organism.reserve', 1.3, 'blessed things survive a drought that kills their neighbours'),
    mul('mortality.stress', 0.8, 'fewer die in the hard year'),
    mul('growth.burst', 0.85, 'nothing leaps — it endures instead'),
  ]),
  epic('life', 'thousand-futures', 'A Thousand Futures', 'Proliferation / adaptation.', [
    mul('reproduction.rate', 1.3, 'more young, everywhere you have been'),
    mul('dispersal.range', 1.4, 'the grove seeds hillsides it never reached'),
    mul('lineage.traitVariation', 1.25, 'descendants stop resembling one another'),
    mul('establishment.marginal', 1.2, 'growth appears on ground that should not carry it'),
    mul('recovery.individual', 0.85, 'a single injured thing mends more slowly'),
  ]),
  epic('life', 'nothing-lives-alone', 'Nothing Lives Alone', 'Ecology / networks.', [
    add('life.work.propagation', 0.3, 'blessing one thing visibly lifts what depends on it'),
    mul('habitat.carryingCapacity', 1.15, 'mixed ground carries more life than its parts should'),
    mul('succession.recoveryRate', 1.25, 'burned ground with survivors comes back fast'),
    mul('life.work.monoculture', 0.8, 'a stand of one thing answers you poorly'),
  ]),

  /* ── Death ───────────────────────────────────────────────────────────── */

  epic('death', 'all-things-return', 'All Things Return', 'Cycling / renewal.', [
    mul('decomposition.rate', 1.6, 'deadfall and carrion vanish into the soil while you watch'),
    mul('nutrient.return', 1.4, 'the ground where things died grows richest'),
    mul('succession.onset', 0.75, 'green returns to a burn a season early'),
    mul('death.work.direct', 0.85, 'you are poor at simply killing'),
  ]),
  epic('death', 'last-mercy', 'The Last Mercy', 'Ending / release.', [
    cost('impulse.cost.lowViability', 0.6, 'ending what is already failing costs almost nothing'),
    mul('decline.duration', 0.5, 'the dying do not linger'),
    mul('witness.fear', 0.75, 'mortals who watch an obvious end are less frightened by it'),
    rule('death.work.healthyTarget', 'against anything healthy you are an ordinary god'),
  ]),
  epic('death', 'only-the-fit-remain', 'Only The Fit Remain', 'Selection / harsh adaptation.', [
    mul('mortality.selectivity', 2, 'it is the weakest that go, twice as reliably'),
    mul('adaptation.pressure', 1.2, 'what survives you is visibly tougher a generation later'),
    mul('mortality.healthy', 0.65, 'the strong come through almost untouched'),
    mul('population.recovery', 0.85, 'numbers come back slowly afterwards'),
  ]),

  /* ── Mortals ─────────────────────────────────────────────────────────── */

  epic('mortals', 'first-witness', 'The First Witness', 'Individuals / lineages.', [
    mul('person.importance', 1.5, 'someone who saw you becomes someone the world keeps'),
    mul('memory.person.halfLife', 2, 'they remember it their whole life'),
    add('lineage.salienceInheritance', 0.25, 'their grandchildren are still marked by it'),
    mul('memory.band.spread', 0.8, 'the community as a whole hears less of it'),
  ]),
  epic('mortals', 'a-people-remember', 'A People Remember', 'Culture / collective memory.', [
    mul('memory.band.halfLife', 2, 'the hearth keeps a story for generations'),
    mul('practice.transmission', 1.3, 'rites and habits pass on intact'),
    mul('culture.driftOnFission', 0.7, 'a daughter people stays recognisably kin far longer'),
    mul('person.promotion', 0.85, 'no single mortal stands out from it'),
  ]),
  epic('mortals', 'human-measure', 'The Human Measure', 'Human literacy.', [
    cost('impulse.cost.mortalTarget', 0.75, 'acting on people is cheap for you'),
    mul('omen.uncertainty.mortal', 0.7, 'your signs are read closer to the way you meant them'),
    mul('pressure.resolution.mortal', 1.5, 'what a band needs, you can see'),
    rule('beast.work.literacy', 'animals and ecology are no easier to you than to anyone'),
  ]),

  /* ── Beasts ──────────────────────────────────────────────────────────── */

  /**
   * The three Beast Epics are the final readings of the three Currents that
   * run through `web.ts`'s BEASTS — herd, blood, voice — and each carries,
   * beside D §9's statistical package (kept, and mostly `absent` still), the
   * qualitative rule that is meant to be its identity, and the lived history
   * the forty-first point asks for. `oracle.ts` says what each qualification
   * would take to answer and what the build is missing to answer it; until
   * then they refuse, in words, and no Beast Epic is reachable in the live
   * game. That is deliberate (PHASE H of the Beasts brief: preparation, not
   * full Epics) — a permanent capstone whose defining rule nothing in `src/`
   * consumes would be the exact thing `WIRING.md` exists to catch.
   */
  {
    ...epic('beasts', 'great-herd', 'The Great Herd',
      'Populations / migrations. Beasts mean collective movement, populations and persistence to me.', [
      // the identity, first: the wide hand on movement lands harder, and the
      // populations of the world report to you (both consumed — bindings.ts)
      mul('beast.work.movement.wide', 1.5, 'a valley-wide press on the herds lands half again as hard'),
      rule('beast.aware.wild', 'the great changes among the herds reach you from anywhere'),
      // D §9's package
      mul('herd.cohesion', 1.3, 'herds hold together instead of scattering'),
      mul('migration.routePersistence', 1.35, 'a crossing stays a crossing for centuries'),
      mul('wildlife.reproduction', 1.15, 'the population climbs where the land allows'),
      mul('animal.promotion', 0.8, 'no one beast becomes a legend'),
    ]),
    current: 'herd',
    qualifies: { id: 'beasts.moved-a-people-of-beasts',
      describes: 'have had your hand on a herd\'s own movement, and had the world go on to call what followed a great migration' },
  },
  {
    ...epic('beasts', 'first-of-the-blood', 'First Of The Blood',
      'Individuals / bloodlines. Beasts mean particular lives and the lines that follow them to me.', [
      rule('beast.care.lineage', 'a line of animals stays an Object of Care past any one life'),
      mul('beast.work.individual', 1.3, 'one animal answers you enormously'),
      add('lineage.imprintInheritance', 0.35, 'its offspring carry something of what you did'),
      mul('things.standing.decayRate', 0.5, 'a promoted animal stays remarkable for lifetimes'),
      mul('beast.work.group', 0.85, 'the herd as a whole barely notices you'),
    ]),
    current: 'blood',
    qualifies: { id: 'beasts.kept-a-line',
      describes: 'have kept one animal line through generations — a descendant the world confirmed carried something of an ancestor you attended' },
  },
  {
    ...epic('beasts', 'wild-speaks', 'The Wild Speaks',
      'Communication / behaviour. Beasts mean minds, learning, communication and remembered behaviour to me.', [
      rule('beast.omen.recognised', 'animals recognise your signs as significant, far more reliably — and still choose for themselves'),
      mul('omen.uncertainty.animal', 0.6, 'animals read your signs nearly correctly'),
      mul('omen.responseLatency', 0.5, 'and act on them at once'),
      mul('memory.animal.halfLife', 2, 'they remember the sign and the place'),
      rule('omen.override.drives', 'panic, hunger and territory still win — you are heard, not obeyed'),
    ]),
    current: 'voice',
    qualifies: { id: 'beasts.taught-the-wild',
      describes: 'have had a sign of yours change what a group of animals did, and had that behaviour outlast the animals that first showed it' },
  },

  /* ── Order ───────────────────────────────────────────────────────────── */

  epic('order', 'office-endures', 'The Office Endures', 'Institutional continuity.', [
    mul('institution.successionFailure', 0.6, 'leadership passes without the community breaking'),
    mul('institution.crisisDuration', 0.5, 'interregnums are short'),
    mul('institution.memoryHalfLife', 2, 'the office remembers what its holders did'),
    mul('institution.innovation', 0.85, 'and changes almost nothing'),
  ]),
  epic('order', 'old-ways', 'The Old Ways', 'Tradition / repeated pattern.', [
    mul('practice.driftRate', 0.65, 'a people keeps its roofs and its rites'),
    mul('pattern.persistence', 1.5, 'routes and rituals outlast the reason for them'),
    mul('practice.adoption', 1.25, 'children do what their parents did'),
    mul('adaptation.abrupt', 0.8, 'and are badly caught when the world changes fast'),
  ]),
  epic('order', 'peace-at-any-price', 'Peace At Any Price', 'Stability with brittleness.', [
    mul('conflict.initiation', 0.65, 'quarrels do not start'),
    mul('band.cohesion', 1.2, 'communities hold'),
    mul('fission.threshold', 1.25, 'they hold past the point they should have split'),
    mul('collapse.magnitude', 1.4, 'and when they finally break, they break badly'),
  ]),

  /* ── Chaos ───────────────────────────────────────────────────────────── */

  epic('chaos', 'no-master', 'No Master', 'Autonomy / decentralisation.', [
    mul('institution.authority', 0.7, 'nobody commands anybody very well'),
    mul('decision.localWeight', 1.3, 'each group decides for itself'),
    mul('fission.propensity', 1.2, 'and leaves when it wants to'),
    mul('innovation.rate', 1.15, 'so more things get tried'),
  ]),
  epic('chaos', 'break-the-throne', 'Break The Throne', 'Collapse of weak structures.', [
    mul('pressure.destabilise.weak', 1.5, 'anything already failing falls fast when you push'),
    mul('institution.recovery', 0.7, 'and does not get back up'),
    mul('event.salience.revolt', 1.25, 'the break itself is remembered'),
    rule('pressure.destabilise.strong', 'against a healthy institution you are nothing in particular'),
  ]),
  epic('chaos', 'become-something-new', 'Become Something New', 'Novelty / divergence.', [
    mul('innovation.cultural', 1.35, 'peoples invent'),
    mul('culture.divergence', 1.3, 'and stop resembling their neighbours'),
    mul('adaptation.variance', 1.25, 'living things vary more widely'),
    mul('pattern.inheritance', 0.8, 'and inherit less of what came before'),
  ]),

  /* ── Reach ───────────────────────────────────────────────────────────── */

  epic('reach', 'high-god', 'The High God', 'Vertical scale / perspective.', [
    mul('presence.comfortableAltitude', 2, 'the sky opens — you rest where you used to strain'),
    mul('presence.maxAltitude', 1.5, 'and can go half again higher than that'),
    mul('focus.maxRadius.fromAltitude', 1.3, 'height buys more attention than it did'),
    mul('presence.speed', 1.1, 'crossing the ground is barely faster, though'),
  ]),
  epic('reach', 'wandering-power', 'The Wandering Power', 'Travel / roaming.', [
    mul('presence.speed.fromAltitude', 2, 'high flight becomes genuinely fast'),
    cost('impulse.cost.altitude', 0.65, 'climbing is cheap'),
    cost('travel.cost.horizontal', 0.7, 'and so is bolting across the island'),
    mul('resonance.gainRate', 0.8, 'but ground you only pass over never learns you'),
  ]),
  epic('reach', 'no-place-is-far', 'No Place Is Far', 'Distant relationships.', [
    mul('perception.objectOfCare.range', 4, 'what you care about stays legible from anywhere'),
    cost('impulse.cost.distance', 0.5, 'distance costs half what it did'),
    mul('act.work.remote', 0.85, 'and you can act on it from outside your own presence'),
    rule('act.remote.knownOnly', 'only on things you already know — strangers are out of reach'),
  ]),

  /* ── Root ────────────────────────────────────────────────────────────── */

  epic('root', 'i-am-here', 'I Am Here', 'Maximum local depth.', [
    mul('resonance.gainRate', 2, 'one place becomes yours twice as fast'),
    cost('act.cost.familiarGround', 0.7, 'and everything you do there is cheap'),
    mul('aura.strength', 1.3, 'your presence works the ground continuously'),
    mul('things.standing.decayRate', 0.75, 'what you made important there stays important'),
    rule('heartland.outside', 'everywhere else, you are an ordinary god'),
  ]),
  epic('root', 'sacred-geography', 'Sacred Geography', 'Distributed sacred network.', [
    cap('anchor.network', 6, 'up to six stones become one holding'),
    mul('anchor.perception', 0.6, 'each sees for you, imperfectly'),
    mul('aura.strength', 0.4, 'each works its ground, weakly'),
    mul('act.work.remote', 0.5, 'and you can act through any of them at half strength'),
  ]),
  epic('root', 'the-land-remembers', 'The Land Remembers', 'Historical persistence.', [
    mul('env.effect.decayRate', 0.5, 'what you did to this ground lasts twice as long'),
    mul('things.standing.decayRate', 0.5, 'and what you made important stays so for lifetimes'),
    mul('history.traceLegibility', 3, 'abandoned holy ground still reads as holy centuries later'),
  ]),

  /* ── Touch ───────────────────────────────────────────────────────────── */

  epic('touch', 'gardener', 'The Gardener', 'Repeated precise care.', [
    add('act.work.repeated', 0.12, 'the tenth time you tend something works far better than the first'),
    rule('act.work.repeated.lapse', 'leave it a year and the accumulated care starts draining away'),
    mul('act.work.wideFocus', 0.85, 'and anything broader than a clearing answers you poorly'),
  ]),
  epic('touch', 'divine-hand', 'The Divine Hand', 'Physical manipulation.', [
    mul('grasp.maxMass', 4, 'you can lift what no god should lift'),
    cost('grasp.cost.attachment', 0.65, 'roots and foundations barely slow you'),
    mul('grasp.damage', 0.6, 'and what you lift survives being lifted'),
    mul('grasp.precision', 1.5, 'and lands where you meant it to'),
  ]),
  epic('touch', 'one-perfect-act', 'One Perfect Act', 'Extreme precision.', [
    mul('act.work.tightFocus', 1.75, 'a cast on one thing is overwhelming'),
    mul('act.collateralArea', 0.5, 'and touches almost nothing around it'),
    cost('act.cost.tightFocus', 1.25, 'it costs more'),
    mul('act.work.repeated', 0.5, 'and doing it twice to the same thing is half wasted'),
  ]),

  /* ── Aura ────────────────────────────────────────────────────────────── */

  epic('aura', 'world-changes', 'Where I Am, The World Changes', 'Ambient intensity.', [
    mul('aura.work', 1.4, 'the ground around you changes while you simply stand in it'),
    mul('aura.radius', 1.25, 'over a wider circle'),
    mul('aura.upkeep', 1.2, 'and it drains you the whole time'),
  ]),
  epic('aura', 'afterglow', 'The Afterglow', 'Persistent wake.', [
    add('aura.afterglow.strength', 0.5, 'the place keeps changing after you leave'),
    add('aura.afterglow.halfLife', 90, 'for a season, fading'),
    cap('aura.afterglow.count', 3, 'in at most three places at once — a fourth lets the weakest go'),
  ]),
  epic('aura', 'attunement', 'Attunement', 'Selective ambient influence.', [
    cap('aura.attunement.count', 3, 'you choose three things your presence is *about*'),
    mul('aura.work.attuned', 1.5, 'and it does those enormously well'),
    mul('aura.crossProcess', 0.3, 'with almost no side effects'),
    mul('aura.work.unattuned', 0.75, 'and does everything else badly'),
  ]),

  /* ── Might ───────────────────────────────────────────────────────────── */

  epic('might', 'by-divine-will', 'By Divine Will', 'Raw force.', [
    mul('act.work', 1.4, 'everything you do lands harder'),
    cost('act.cost', 1.2, 'and costs more'),
    add('process.viabilityFloor', -0.15, 'and you can force processes the land does not really support'),
  ]),
  epic('might', 'unbreakable-purpose', 'Unbreakable Purpose', 'Sustained exertion.', [
    mul('resistance.effect', 0.3, 'opposition barely slows a sustained act'),
    cost('act.cost.sustained', 0.75, 'holding something is cheap'),
    mul('act.work.burst', 0.85, 'starting it is weak'),
  ]),
  epic('might', 'the-great-act', 'The Great Act', 'Rare historical miracle.', [
    cap('act.impulseShare', 1, 'you may spend everything you have on one act'),
    mul('act.work', 2.5, 'and it is enormous'),
    mul('focus.capOverride', 1.5, 'and larger than your Focus should allow'),
    mul('impulse.regen', 0.25, 'and then you are almost nothing for a world year'),
  ]),

  /* ── Insight ─────────────────────────────────────────────────────────── */

  epic('insight', 'by-natural-law', 'By Natural Law', 'Leverage.', [
    mul('act.work.highViability', 1.35, 'working with the land is dramatically stronger'),
    cost('act.cost.highViability', 0.75, 'and cheaper'),
    rule('act.work.lowViability', 'and against the land you get nothing extra at all'),
  ]),
  epic('insight', 'far-consequence', 'The Far Consequence', 'Forecasting.', [
    mul('foresight.horizon', 4, 'you see much further down the chain'),
    cap('foresight.branches', 3, 'and see three ways it could go, not one'),
    mul('act.work.leveraged', 1.2, 'acting on the right point works better'),
    mul('act.collateralMagnitude', 0.8, 'and breaks less by accident'),
  ]),
  epic('insight', 'hidden-thread', 'The Hidden Thread', 'Systems / causal chains.', [
    add('act.work.propagation', 0.2, 'a fifth of what you do travels to what depends on it'),
    cap('act.work.propagationHops', 3, 'three links deep, halving each time'),
  ]),

  /* ── Sovereignty ─────────────────────────────────────────────────────── */

  epic('sovereignty', 'i-alone-am-enough', 'I Alone Am Enough', 'Intrinsic power.', [
    mul('impulse.regen', 1.3, 'you refill faster than any god who needs anyone'),
    rule('impulse.regen.floor', 'and never depend on being believed in'),
    mul('faith.contribution', 0.25, 'belief barely adds to you'),
  ]),
  epic('sovereignty', 'unseen-god', 'The Unseen God', 'Unwitnessed divinity.', [
    cost('impulse.cost.unwitnessed', 0.75, 'acting where nobody can see is cheap'),
    mul('event.salience.divine', 0.6, 'and leaves less of a mark on anyone who might'),
    rule('act.work.unwitnessed', 'the world changes exactly as much either way'),
    mul('faith.fromUnwitnessed', 0, 'and nobody believes anything until they find the consequence'),
  ]),
  epic('sovereignty', 'sovereign-will', 'Sovereign Will', 'Identity resistance.', [
    mul('rivalInfluence.effect', 0.5, 'another god can barely touch your presence or your stones'),
    mul('faith.contribution', 0.25, 'what mortals believe changes little about you'),
    mul('faith.hostile', 0.5, 'including what they believe against you'),
    mul('communion.benefit', 0.7, 'and you gain less from any of them'),
  ]),

  /* ── Communion ───────────────────────────────────────────────────────── */

  epic('communion', 'through-many-hands', 'Through Many Hands', 'Voluntary distributed action.', [
    mul('mortal.labour', 1.25, 'people working in your tradition build faster'),
    mul('structure.maintenance', 1.2, 'and keep it standing longer'),
    mul('faith.fromStewardship', 1.2, 'and believe harder for having done it'),
    rule('mortal.labour.coerced', 'none of which happens if you made them'),
  ]),
  epic('communion', 'many-eyes', 'Many Eyes', 'Distributed perception.', [
    rule('perception.viaBelievers', 'believers tell you what happened near them'),
    add('perception.reportLatency', 6, 'within about six world hours'),
    add('perception.reportAccuracy', 0.7, 'roughly right, and righter when several saw it'),
    rule('perception.viaBelievers.detail', 'you cannot look closer — only be told'),
  ]),
  epic('communion', 'god-made-of-stories', 'A God Made Of Stories', 'Mythology as power and risk.', [
    mul('faith.fromMemory', 1.4, 'being remembered is itself power'),
    mul('memory.band.halfLife', 2, 'and they remember twice as long'),
    mul('religion.transmission', 1.3, 'and pass it on'),
    add('faith.fromMisattribution', 0.5, 'including the things you never did'),
  ]),
];

const BY_DIRECTION = new Map<DirectionId, Epic[]>();
for (const e of EPICS) {
  const list = BY_DIRECTION.get(e.direction) ?? [];
  list.push(e);
  BY_DIRECTION.set(e.direction, list);
}

export const epicsOf = (d: DirectionId): readonly Epic[] => BY_DIRECTION.get(d) ?? [];
export const epicById = (id: string): Epic | undefined => EPICS.find((e) => e.id === id);
